import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroSquares2x2,
  heroMagnifyingGlass,
  heroRss,
  heroBriefcase,
  heroMapPin,
  heroDocumentText,
  heroChartBar,
  heroCog6Tooth,
  heroChevronLeft,
  heroChevronRight,
  heroPlusCircle,
  heroShieldCheck
} from '@ng-icons/heroicons/outline';
import { LayoutService } from '../../services/layout.service';
import { AuthService } from '../../../auth/services/auth.service';
import { AdminService } from '../../../features/admin/services/admin.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, NgIcon],
  viewProviders: [
    provideIcons({
      heroSquares2x2,
      heroMagnifyingGlass,
      heroRss,
      heroBriefcase,
      heroMapPin,
      heroDocumentText,
      heroChartBar,
      heroCog6Tooth,
      heroChevronLeft,
      heroChevronRight,
      heroPlusCircle,
      heroShieldCheck
    })
  ],
  template: `
    <aside 
      class="h-screen bg-white border-r border-gray-300 enterprise-shadow flex flex-col transition-width"
      [class.sidebar-collapsed]="layoutService.sidebarCollapsed()"
      [class.sidebar-expanded]="!layoutService.sidebarCollapsed()">
      
      <!-- Logo section -->
      <div class="h-16 flex items-center border-b border-gray-300"
           [class.justify-center]="layoutService.sidebarCollapsed()"
           [class.justify-between]="!layoutService.sidebarCollapsed()"
           [class.px-4]="!layoutService.sidebarCollapsed()"
           [class.px-2]="layoutService.sidebarCollapsed()">
        <div class="flex items-center gap-3">
          <!-- FilterLabs Logo -->
          <div class="w-8 h-8 flex-shrink-0">
            <img src="assets/images/FilterLabsIcon.png" alt="FilterLabs Logo" class="w-full h-full object-contain" />
          </div>
          <div class="sidebar-text" [class.hidden]="layoutService.sidebarCollapsed()">
            <span class="font-gloock text-lg font-semibold text-slate-800">FilterLabs</span>
          </div>
        </div>
        
        <!-- Collapse button -->
        <button 
          *ngIf="!layoutService.sidebarCollapsed()"
          (click)="layoutService.toggleSidebar()"
          class="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors">
          <ng-icon name="heroChevronLeft" class="h-4 w-4" />
        </button>
      </div>
      
      <!-- Expand button when collapsed -->
      <div *ngIf="layoutService.sidebarCollapsed()" class="p-2 border-b border-gray-300">
        <button 
          (click)="layoutService.toggleSidebar()"
          class="w-full p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors flex justify-center">
          <ng-icon name="heroChevronRight" class="h-4 w-4" />
        </button>
      </div>
      
      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto"
           [class.px-3]="!layoutService.sidebarCollapsed()"
           [class.px-1]="layoutService.sidebarCollapsed()"
           [class.py-4]="!layoutService.sidebarCollapsed()"
           [class.py-2]="layoutService.sidebarCollapsed()">
        <div *ngFor="let section of layoutService.navigationSections()" class="mb-6">
          <!-- Section title -->
          <div 
            *ngIf="!layoutService.sidebarCollapsed()"
            class="px-3 mb-2">
            <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">
              {{ section.title }}
            </p>
          </div>
          
          <!-- Navigation items -->
          <div *ngFor="let item of section.items" class="mb-1">
            <a 
              [routerLink]="item.route"
              routerLinkActive="bg-blue-50 text-blue-700 border border-blue-200"
              [routerLinkActiveOptions]="{ exact: item.id === 'create' }"
              class="flex items-center text-sm font-medium rounded-lg transition-colors group relative text-slate-700 hover:bg-slate-100"
              [class.px-3]="!layoutService.sidebarCollapsed()"
              [class.py-2.5]="!layoutService.sidebarCollapsed()"
              [class.px-2]="layoutService.sidebarCollapsed()"
              [class.py-3]="layoutService.sidebarCollapsed()"
              [class.justify-center]="layoutService.sidebarCollapsed()"
              [title]="layoutService.sidebarCollapsed() ? item.label : ''">
              
              <!-- Icon -->
              <ng-icon [name]="item.icon" class="h-5 w-5 flex-shrink-0" />
              
              <!-- Label and badge -->
              <span class="sidebar-text ml-3" [class.hidden]="layoutService.sidebarCollapsed()">
                {{ item.label }}
              </span>
              
              <!-- Badge for expanded -->
              <span 
                *ngIf="item.badge && !layoutService.sidebarCollapsed()" 
                class="sidebar-text ml-auto text-xs px-2 py-0.5 rounded-full"
                [class]="getBadgeClasses(item.badge.color)">
                {{ item.badge.text }}
              </span>
              
              <!-- Badge dot for collapsed -->
              <span 
                *ngIf="item.badge && layoutService.sidebarCollapsed()" 
                class="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
                [class]="getBadgeClasses(item.badge.color)">
                <span class="w-2 h-2 rounded-full bg-current"></span>
              </span>
            </a>
          </div>
        </div>

        <!-- Admin section (only for users with an admin capability) -->
        <div *ngIf="adminService.hasAnyCapability()" class="mb-6">
          <div *ngIf="!layoutService.sidebarCollapsed()" class="px-3 mb-2">
            <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">ADMIN</p>
          </div>
          <div class="mb-1">
            <a
              routerLink="/admin"
              routerLinkActive="bg-blue-50 text-blue-700 border border-blue-200"
              [routerLinkActiveOptions]="{ paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
              class="flex items-center text-sm font-medium rounded-lg transition-colors group relative text-slate-700 hover:bg-slate-100"
              [class.px-3]="!layoutService.sidebarCollapsed()"
              [class.py-2.5]="!layoutService.sidebarCollapsed()"
              [class.px-2]="layoutService.sidebarCollapsed()"
              [class.py-3]="layoutService.sidebarCollapsed()"
              [class.justify-center]="layoutService.sidebarCollapsed()"
              [title]="layoutService.sidebarCollapsed() ? 'Admin' : ''">
              <ng-icon name="heroShieldCheck" class="h-5 w-5 flex-shrink-0" />
              <span class="sidebar-text ml-3" [class.hidden]="layoutService.sidebarCollapsed()">Admin</span>
            </a>
          </div>
        </div>
      </nav>

      <!-- User Profile & Branding section -->
      <div class="border-t border-gray-300"
           [class.p-3]="!layoutService.sidebarCollapsed()"
           [class.p-2]="layoutService.sidebarCollapsed()">
        <div class="flex items-center rounded-lg transition-colors group"
             [class.justify-center]="layoutService.sidebarCollapsed()"
             [class.p-2]="!layoutService.sidebarCollapsed()"
             [class.justify-center]="layoutService.sidebarCollapsed()">
           
          <!-- Avatar (Clickable) -->
          <a routerLink="/account" 
             class="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" 
             [class.mr-3]="!layoutService.sidebarCollapsed()"
             title="Go to Account Settings">
            <div class="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white text-xs font-semibold shadow-sm ring-2 ring-white border border-violet-100">
              {{ userInitials }}
            </div>
            <div class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></div>
          </a>

          <!-- Branding Info (Static) -->
          <div class="sidebar-text min-w-0 flex-1" [class.hidden]="layoutService.sidebarCollapsed()">
            <p class="text-sm font-semibold text-slate-800 truncate leading-tight">Ubiquity</p>
            <p class="text-[10px] text-slate-500 font-medium truncate">a FilterLabs Product</p>
          </div>
          
        </div>
      </div>
    </aside>
  `,
  styles: [`
    .sidebar-collapsed { width: 60px; }
    .sidebar-expanded { width: 240px; }
    .transition-width { transition: width 0.2s ease-in-out; }
    .enterprise-shadow { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); }
  `]
})
export class SidebarComponent implements OnInit {
  layoutService = inject(LayoutService);
  adminService = inject(AdminService);
  private authService = inject(AuthService);

  userInitials = 'U';
  userFullName = 'User';
  username = '';

  ngOnInit() {
    // Load admin capabilities so the Admin nav entry appears for admins on any page.
    this.adminService.ensureCapabilities().subscribe();

    this.authService.getUserProfile().subscribe(profile => {
      if (profile) {
        this.username = profile.username || '';
        const firstName = profile.firstName || '';
        const lastName = profile.lastName || '';

        this.userFullName = `${firstName} ${lastName}`.trim() || this.username || 'User';

        // Calculate initials
        if (firstName && lastName) {
          this.userInitials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        } else if (firstName && firstName.length >= 2) {
          this.userInitials = firstName.substring(0, 2).toUpperCase();
        } else if (this.username && this.username.length >= 2) {
          this.userInitials = this.username.substring(0, 2).toUpperCase();
        }
      }
    });
  }

  getBadgeClasses(color: string): string {
    const colorMap: { [key: string]: string } = {
      'primary': 'bg-blue-100 text-blue-600',
      'secondary': 'bg-slate-100 text-slate-600',
      'accent': 'bg-purple-100 text-purple-600',
      'error': 'bg-red-100 text-red-600',
      'warning': 'bg-amber-100 text-amber-600',
      'success': 'bg-emerald-100 text-emerald-600'
    };
    return colorMap[color] || 'bg-slate-100 text-slate-600';
  }
}
